/**
 * logger.ts
 *
 * Runtime-agnostic structured logger. Provides log level filtering and
 * structured output, and forwards `error()` calls to an optional forwarder
 * that server entry points register at startup.
 *
 * Why the forwarder indirection: `logger.ts` is reachable from browser
 * bundles (e.g. client components that call `createLogger`). Directly
 * importing `posthog-node` here would drag Node-only built-ins (`node:fs`,
 * `node:readline`) into the client bundle. Instead, server-only code
 * (Lambda wrappers, Next.js `instrumentation.ts`) calls
 * `setErrorForwarder(captureServerException)` at boot. In client bundles no
 * forwarder is ever registered, so `logger.error()` remains a console-only
 * no-op.
 *
 * Forwarding is fire-and-forget and never throws — PostHog events are
 * flushed at handler exit via `withObservability`.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

/**
 * Severity label used in formatted output. `audit` is deliberately not a
 * `LogLevel` — it is never filtered, so it has no place in the ordering.
 * See `Logger.audit`.
 */
type LogLabel = LogLevel | "audit";

export interface ErrorForwarderContext {
  distinctId: string;
  properties?: Record<string, unknown>;
  groups?: Record<string, string>;
}

export type ErrorForwarder = (
  error: unknown,
  context: ErrorForwarderContext,
) => void;

let errorForwarder: ErrorForwarder | null = null;

/**
 * Register a forwarder for `logger.error()`. Called once per runtime at
 * server startup (e.g. from `withObservability` for Lambdas, or a Next.js
 * `instrumentation.ts#register()` for the Next.js server runtime). Pass
 * `null` to clear (test-only).
 *
 * Safe to call from any module — if the module is tree-shaken out of the
 * client bundle, the forwarder simply never registers and `logger.error()`
 * stays console-only.
 */
export function setErrorForwarder(fn: ErrorForwarder | null): void {
  errorForwarder = fn;
}

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function parseLogLevel(value: string | undefined): LogLevel | undefined {
  const normalized = value?.trim().toLowerCase();
  if (
    normalized &&
    Object.prototype.hasOwnProperty.call(LOG_LEVEL_PRIORITY, normalized)
  ) {
    return normalized as LogLevel;
  }
  return undefined;
}

// Resolve the active log level. Browser bundles only see NEXT_PUBLIC_* vars
// (Next.js inlines those at build time; bare process.env.LOG_LEVEL resolves
// to undefined client-side), so prefer the public name and fall back to the
// server-only LOG_LEVEL when unset. When neither is set, default by stage:
// 'warn' in prod (suppress noisy info logs in production browser consoles),
// 'info' everywhere else.
function resolveLogLevel(): LogLevel {
  const explicit =
    parseLogLevel(process.env.NEXT_PUBLIC_LOG_LEVEL) ??
    parseLogLevel(process.env.LOG_LEVEL);
  if (explicit) return explicit;
  const stage = process.env.NEXT_PUBLIC_SST_STAGE ?? process.env.SST_STAGE;
  return stage === "prod" ? "warn" : "info";
}

const CURRENT_LOG_LEVEL: LogLevel = resolveLogLevel();

interface LogContext {
  [key: string]: unknown;
}

// Cap the captured response body so a large HTML/error page from an upstream
// can't bloat a single CloudWatch log line.
const MAX_RESPONSE_BODY_CHARS = 2000;

function stringifyResponseBody(data: unknown): string {
  if (typeof data === "string") return data;
  try {
    return JSON.stringify(data);
  } catch {
    return "[unserializable response body]";
  }
}

/**
 * Pull HTTP response details off an axios-shaped error so failed outbound
 * requests are diagnosable from CloudWatch. `error instanceof Error` keeps only
 * `message` + `stack`; for an AxiosError that drops `response.status` and
 * `response.data`, which for several integrations (e.g. PowerBI
 * `executeQueries`) is the ONLY place the upstream's real reason appears.
 *
 * Detected structurally (no `axios` import) because this module is reachable
 * from client bundles. Deliberately never reads `config`/`headers` — those
 * carry the bearer token — and truncates the body to bound log size.
 */
function extractHttpErrorFields(error: unknown): LogContext {
  if (!error || typeof error !== "object") return {};
  const candidate = error as { isAxiosError?: unknown; response?: unknown };
  if (candidate.isAxiosError !== true) return {};
  const response = candidate.response;
  if (!response || typeof response !== "object") return {};
  const { status, data } = response as { status?: unknown; data?: unknown };
  const fields: LogContext = {};
  if (typeof status === "number") fields.httpStatus = status;
  if (data !== undefined) {
    const serialized = stringifyResponseBody(data);
    fields.responseBody =
      serialized.length > MAX_RESPONSE_BODY_CHARS
        ? `${serialized.slice(0, MAX_RESPONSE_BODY_CHARS)}…[truncated]`
        : serialized;
  }
  return fields;
}

class Logger {
  private prefix: string;

  constructor(prefix: string) {
    this.prefix = prefix;
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[CURRENT_LOG_LEVEL];
  }

  private safeStringify(value: unknown): string {
    const seen = new WeakSet<object>();
    try {
      return JSON.stringify(value, (_key, v) => {
        if (typeof v === "bigint") return v.toString();
        if (v instanceof Error) return { message: v.message, stack: v.stack };
        if (typeof v === "object" && v !== null) {
          const obj = v as object;
          if (seen.has(obj)) return "[Circular]";
          seen.add(obj);
        }
        return v;
      });
    } catch (e) {
      return `[Unserializable context: ${e instanceof Error ? e.message : "unknown error"}]`;
    }
  }

  private formatMessage(label: LogLabel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` ${this.safeStringify(context)}` : "";
    return `${timestamp} [${label.toUpperCase()}] [${this.prefix}] ${message}${contextStr}`;
  }

  /**
   * Record a security- or lifecycle-critical event that must survive the
   * configured log level.
   *
   * Why this exists: production resolves to `warn` (see `resolveLogLevel`),
   * which silently dropped every successful member deactivation. The outcome
   * logs in `UserDeactivationService` were `info`, and the WorkOS DSync
   * webhook logged only side-effect *failures* — so a deactivation that
   * worked perfectly left no trace at any level. Three production
   * deactivations were reconstructable only from an incidental Recall
   * metric, and a customer's "who deactivated this user?" went unanswered
   * (BRE-5899).
   *
   * Deliberately NOT an entry in `LOG_LEVEL_PRIORITY`: this is not "more
   * severe than error", it is orthogonal to severity — an operational record
   * that is never noise. Reserve it for events an operator must be able to
   * reconstruct after the fact (membership lifecycle, access revocation).
   * Anything you would be willing to lose belongs at `info`.
   */
  audit(message: string, context?: LogContext): void {
    console.log(this.formatMessage("audit", message, context));
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog("debug")) {
      console.debug(this.formatMessage("debug", message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog("info")) {
      console.log(this.formatMessage("info", message, context));
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog("warn")) {
      console.warn(this.formatMessage("warn", message, context));
    }
  }

  error(message: string, error?: unknown, context?: LogContext): void {
    if (this.shouldLog("error")) {
      let errorFields: LogContext = {};
      if (error instanceof Error) {
        errorFields = {
          errorMessage: error.message,
          stack: error.stack,
          // Adds httpStatus + responseBody for axios errors so 4xx/5xx from
          // upstreams (PowerBI, etc.) are diagnosable from CloudWatch; no-op
          // for ordinary Errors.
          ...extractHttpErrorFields(error),
        };
      } else if (error !== undefined) {
        // Assign raw value — safeStringify (called by formatMessage) handles
        // circular refs, bigints, and nested Error objects via its replacer.
        errorFields = { error };
      }
      console.error(this.formatMessage("error", message, { ...context, ...errorFields }));
    }

    // Forward to the registered error sink (PostHog Error Tracking on the
    // server) ONLY when the log context carries a real user identifier
    // (userUuid / userId / user_uuid / user_id). Without one, the forwarded
    // event would land on a synthetic `system:<stage>:<prefix>` distinct_id
    // and make the Slack "View user" deep link look legitimate but resolve
    // to an empty orphan Person page — deceptive UX worse than having no
    // button. Local console.error still fires for CloudWatch visibility;
    // the error is recorded, just not surfaced as a `$exception` in the
    // PostHog Error Tracking UI.
    //
    // Call sites that want PostHog tracking must pass user identity in
    // context. Background jobs / crons without user context fall through
    // to CloudWatch-only by design.
    //
    // No-op when nothing is registered — e.g. in client bundles, in unit
    // tests that don't wire a forwarder, or when POSTHOG_API_KEY is unset
    // and the server forwarder self-no-ops.
    const userDistinctId = pickUserDistinctId(context);
    if (!userDistinctId) return;
    const forwarder = errorForwarder;
    if (forwarder) {
      try {
        forwarder(error ?? new Error(message), {
          distinctId: userDistinctId,
          properties: {
            logger_prefix: this.prefix,
            logger_message: message,
            stage: process.env.SST_STAGE ?? "local",
            function_name: process.env.AWS_LAMBDA_FUNCTION_NAME ?? undefined,
            trace_id: process.env._X_AMZN_TRACE_ID ?? undefined,
            ...(context ?? {}),
          },
          groups: resolveGroups(context),
        });
      } catch {
        // Logger must never throw. A well-behaved forwarder already catches
        // internally; this is belt-and-suspenders for unexpected failures
        // during distinctId/group resolution or a buggy forwarder.
      }
    }
  }
}

/**
 * Pick the first non-empty user identifier from the log context — the value
 * that should become the PostHog distinct_id on forwarded events. orgUuid
 * is deliberately excluded: using it would produce an orphan Person whose
 * Slack "View user" deep link 404s (orgs live under $groups, not Persons).
 *
 * Returns `undefined` when no user identity is present, which also doubles
 * as the gate that suppresses PostHog forwarding — the `Logger.error` call
 * bails on undefined so events without a real user never reach the
 * forwarder, preventing synthetic `system:<stage>:<prefix>` distinctIds
 * from showing up in Error Tracking at all.
 */
function pickUserDistinctId(context: LogContext | undefined): string | undefined {
  if (!context) return undefined;
  const candidates = [
    context.userUuid,
    context.userId,
    context.user_uuid,
    context.user_id,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.length > 0) return candidate;
  }
  return undefined;
}

function resolveGroups(context: LogContext | undefined): Record<string, string> | undefined {
  if (!context) return undefined;
  const orgUuid =
    pickString(context.organizationUuid) ??
    pickString(context.organization_uuid) ??
    pickString(context.orgUuid) ??
    pickString(context.org_uuid);
  if (!orgUuid) return undefined;
  return { organization: orgUuid };
}

function pickString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Create a logger with a specific prefix/module name.
 */
export function createLogger(prefix: string): Logger {
  return new Logger(prefix);
}
